import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router'
import { LayoutDashboard, Menu, X } from 'lucide-react'
import { race, registrationPhase } from '../../shared/race.config'
import { formatLongDate, formatClock } from '../../shared/format'
import { DEMO } from '../lib/api'
import { storage } from '../lib/storage'
import { Wordmark } from './brand'
import { RegisterButton } from './race'
import { withOrdinals } from '../lib/ordinals'

export const NAV = [
  { to: '/info', label: 'Race info' },
  { to: '/course', label: 'Course' },
  { to: '/donate', label: 'Donate' },
  { to: '/volunteer', label: 'Volunteer' },
  { to: '/faq', label: 'FAQ' },
]

function ScrollToTop() {
  const { pathname, hash } = useLocation()
  useEffect(() => {
    if (hash) {
      // Wait a frame for lazily loaded pages to render their sections.
      const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' }), 60)
      return () => clearTimeout(t)
    }
    window.scrollTo({ top: 0 })
  }, [pathname, hash])
  return null
}

function DemoBanner() {
  const [hidden, setHidden] = useState(() => storage.get<boolean>('bw5k-demo-banner-hidden') === true)
  if (!DEMO || hidden) return null
  return (
    <div className="border-b border-warn/30 bg-warn-soft text-sm text-ink">
      <div className="container-page flex flex-wrap items-center gap-x-4 gap-y-1 py-2">
        <p className="min-w-0 grow basis-[min(100%,34rem)]">
          <strong className="font-semibold">Preview build.</strong> Dates, prices and policies are sample content, and payments are simulated:
          nothing is charged and nothing leaves your browser.
        </p>
        <Link to="/admin" className="inline-flex items-center gap-1.5 font-semibold text-brand underline-offset-2 hover:underline">
          <LayoutDashboard className="size-4" aria-hidden /> Race director dashboard
        </Link>
        <button
          type="button"
          onClick={() => {
            storage.set('bw5k-demo-banner-hidden', true)
            setHidden(true)
          }}
          className="rounded p-1 text-ink-2 hover:bg-warn/15"
          aria-label="Hide preview notice"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  )
}

function Header() {
  const [open, setOpen] = useState(false)
  const { pathname } = useLocation()
  const panelRef = useRef<HTMLDivElement>(null)
  useEffect(() => setOpen(false), [pathname])
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('keydown', onKey)
    panelRef.current?.querySelector<HTMLElement>('a')?.focus()
    return () => document.removeEventListener('keydown', onKey)
  }, [open])
  const inRegister = pathname.startsWith('/register')

  return (
    <header className="sticky top-[env(safe-area-inset-top,0px)] z-40 border-b border-line bg-bg/90 backdrop-blur supports-[backdrop-filter]:bg-bg/80">
      <div className="container-page flex h-16 items-center gap-4">
        <Wordmark />
        <nav aria-label="Main" className="ml-auto hidden items-center gap-1 lg:flex">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `rounded-md px-3 py-2 text-[0.95rem] font-medium transition-colors ${isActive ? 'text-brand' : 'text-ink-2 hover:text-ink'}`
              }
            >
              {({ isActive }) => (
                <span className={`border-b-2 pb-1 ${isActive ? 'border-accent-bright' : 'border-transparent'}`}>{n.label}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2 lg:ml-2">
          {!inRegister && (
            <RegisterButton size="md" className="px-4 sm:px-5">
              Register
            </RegisterButton>
          )}
          <button
            type="button"
            className="grid size-11 place-items-center rounded-lg text-ink hover:bg-surface-2 lg:hidden"
            aria-expanded={open}
            aria-controls="mobile-nav"
            aria-label={open ? 'Close menu' : 'Open menu'}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="size-6" /> : <Menu className="size-6" />}
          </button>
        </div>
      </div>
      <div id="mobile-nav" ref={panelRef} hidden={!open} className="border-t border-line bg-bg lg:hidden">
        <nav aria-label="Main" className="container-page flex flex-col py-2">
          {[{ to: '/', label: 'Home' }, ...NAV, { to: '/participants', label: 'Who’s running' }, { to: '/results', label: 'Results' }].map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/'}
              className={({ isActive }) => `rounded-md px-2 py-3 text-lg font-medium ${isActive ? 'text-brand' : 'text-ink'}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </header>
  )
}

function Footer() {
  return (
    <footer className="mt-24 border-t border-line bg-surface">
      <div className="border-b border-line bg-brand-soft">
        <p className="container-page py-3 text-sm text-ink">
          <strong className="font-semibold">Struggling, or worried about someone?</strong> Call or text <strong className="font-semibold">988</strong>{' '}
          (Suicide & Crisis Lifeline), or text HOME to 741741. In an emergency, call 911.{' '}
          <a href="https://988lifeline.org" target="_blank" rel="noopener noreferrer" className="font-semibold text-brand underline-offset-2 hover:underline">
            988lifeline.org
          </a>
        </p>
      </div>
      <div className="container-page grid gap-10 py-12 md:grid-cols-[1.4fr_1fr_1fr]">
        <div className="flex flex-col gap-3">
          <Wordmark />
          <p className="text-ink-2">
            {withOrdinals(formatLongDate(race.startsAt))} · 5K starts {formatClock(race.events[0].startTime)}
            <br />
            {race.location.venue} ({race.location.formalName}), {race.location.areaLine}
          </p>
          <p className="text-sm text-ink-3">
            Questions? Email <span className="select-all font-medium text-ink-2">{race.organizer.email}</span>
          </p>
        </div>
        <nav aria-label="Footer" className="grid grid-cols-2 gap-x-6 gap-y-2 text-[0.95rem] md:grid-cols-1">
          <Link className="text-ink-2 hover:text-ink" to="/register">{registrationPhase() === 'upcoming' ? 'Get notified when registration opens' : 'Register'}</Link>
          <Link className="text-ink-2 hover:text-ink" to="/donate">Donate</Link>
          <Link className="text-ink-2 hover:text-ink" to="/info">Race info</Link>
          <Link className="text-ink-2 hover:text-ink" to="/course">Course map</Link>
          <Link className="text-ink-2 hover:text-ink" to="/participants">Who’s running</Link>
          <Link className="text-ink-2 hover:text-ink" to="/results">Results</Link>
          <Link className="text-ink-2 hover:text-ink" to="/volunteer">Volunteer</Link>
          <Link className="text-ink-2 hover:text-ink" to="/faq">FAQ</Link>
        </nav>
        <div className="flex flex-col gap-2 text-[0.95rem]">
          <Link className="text-ink-2 hover:text-ink" to="/policies#waiver">Waiver</Link>
          <Link className="text-ink-2 hover:text-ink" to="/policies#refunds">Refunds & transfers</Link>
          <Link className="text-ink-2 hover:text-ink" to="/policies#privacy">Privacy notice</Link>
          <p className="mt-3 text-sm text-ink-3">Payments are processed securely by Stripe. We never see or store card numbers.</p>
        </div>
      </div>
      <div className="border-t border-line">
        <div className="container-page flex flex-wrap justify-between gap-2 py-4 text-xs text-ink-3">
          <span>© {new Date(race.startsAt).getFullYear()} {race.organizer.name}</span>
        </div>
      </div>
    </footer>
  )
}

export function Layout() {
  return (
    <div className="flex min-h-screen flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-50 focus:rounded-md focus:bg-surface focus:px-4 focus:py-2 focus:shadow-card">
        Skip to content
      </a>
      <ScrollToTop />
      <DemoBanner />
      <Header />
      <main id="main" className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  )
}
